import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle, CalendarClock, Scale, TrendingDown, Check, Loader2,
  ChevronDown, ChevronRight, Activity, EyeOff, Eye, Settings2,
} from "lucide-react";
import { fmtMoney } from "@/lib/currency";
import { notesApi } from "@/lib/api";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuCheckboxItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { RIGHT_NOW_SECTIONS } from "@/lib/hqPrefs";

const PREVIEW = { overdue: 5, upcoming: 6, decisions: 4, warnings: 5, health: 5 };

/**
 * Section — collapsible Right Now section with a hide/show settings entry.
 *
 * `collapsed`/`onToggleCollapsed` are the per-section fold state (persisted
 * in localStorage via hqPrefs). Hidden sections are filtered out ONE level up
 * in `AttentionPanel` — the hide-toggle here lives only in the top-of-panel
 * settings menu so the panel never renders a hidden section at all.
 */
function Section({ sectionKey, title, count, collapsed, onToggleCollapsed, children }) {
  return (
    <div className="mb-6" data-testid={`attention-section-${sectionKey}`}>
      <button
        type="button"
        onClick={onToggleCollapsed}
        className="w-full flex items-center justify-between mb-3 group"
        data-testid={`attention-section-toggle-${sectionKey}`}
        aria-expanded={!collapsed}
      >
        <div className="flex items-center gap-1.5 text-eyebrow group-hover:text-white/80 transition-colors">
          {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          <span>{title}</span>
        </div>
        <span className="text-[10px] text-white/40 px-1.5 py-0.5 rounded border border-white/10 tabular-nums">{count}</span>
      </button>
      {!collapsed && <div className="space-y-2">{children}</div>}
    </div>
  );
}

function ViewAllToggle({ expanded, total, previewCount, onToggle, testid }) {
  if (total <= previewCount) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testid}
      className="mt-1 w-full flex items-center justify-center gap-1 text-[11px] uppercase tracking-wider text-white/50 hover:text-white/90 transition-colors py-1.5 rounded-md hover:bg-white/[0.04]"
    >
      <ChevronDown
        className="w-3 h-3 transition-transform"
        style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
      />
      {expanded ? "Show less" : `View all ${total}`}
    </button>
  );
}

function Item({ icon: Icon, iconColor, primary, secondary, to, right, testid }) {
  const inner = (
    <div className="flex flex-col gap-1 p-3 rounded-lg border border-white/5 bg-white/[0.04] hover:bg-white/[0.08] transition-colors" data-testid={testid}>
      <div className="flex items-start gap-2">
        <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: iconColor }} />
        <div className="flex-1 min-w-0 text-sm text-white/90 leading-snug break-words">{primary}</div>
        {right && <div className="flex-shrink-0">{right}</div>}
      </div>
      {secondary && <div className="text-xs text-white/40 pl-5 break-words">{secondary}</div>}
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

const SOURCE_META = {
  event: { label: "Meeting", color: "#3B82F6", route: () => `/calendar` },
  task: { label: "Task due", color: "#A78BFA", route: () => `/tasks` },
  project: { label: "Project due", color: "#22D3EE", route: (i) => `/entity/${i.entity_id}` },
};

function TypeBadge({ type }) {
  const meta = SOURCE_META[type] || SOURCE_META.event;
  return (
    <span
      className="inline-flex items-center text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border"
      style={{ color: meta.color, borderColor: `${meta.color}55`, backgroundColor: `${meta.color}12` }}
      data-testid={`attention-badge-${type}`}
    >
      {meta.label}
    </span>
  );
}

function DecisionItem({ note, onResolved }) {
  const [busy, setBusy] = useState(false);
  const markResolved = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBusy(true);
    try {
      await notesApi.resolve(note.id, true);
      toast.success("Decision marked resolved");
      onResolved && onResolved(note.id);
    } catch {
      toast.error("Could not update decision");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-1 p-3 rounded-lg border border-white/5 bg-white/[0.04] hover:bg-white/[0.08] transition-colors" data-testid={`attention-decision-${note.id}`}>
      <div className="flex items-start gap-2">
        <Scale className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: "#F59E0B" }} />
        <Link to={`/entity/${note.entity_id}`} className="flex-1 text-sm text-white/90 leading-snug hover:text-white">
          {note.title}
        </Link>
        <button
          onClick={markResolved}
          disabled={busy}
          className="flex-shrink-0 flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-md border border-white/10 bg-white/[0.04] text-white/60 hover:text-white hover:bg-white/10 hover:border-white/20 transition-colors disabled:opacity-50"
          data-testid={`attention-resolve-btn-${note.id}`}
          title="Mark as resolved"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          Resolve
        </button>
      </div>
      <div className="text-xs text-white/40 pl-5">
        {note.date || new Date(note.created_at).toLocaleDateString()}
      </div>
    </div>
  );
}

function WarningItem({ w, kind }) {
  const to = `/entity/${w.entity_id}`;
  const primary = kind === "project"
    ? <><span className="text-white/60">{w.entity_name}</span> · {w.project_name} <span className="text-white/50">at {w.pct}%</span></>
    : <><span>{w.entity_name}</span> <span className="text-white/50">at {w.pct}%</span></>;
  const overAmt = w.over ? (w.spent - w.approved) : 0;
  return (
    <Item
      icon={TrendingDown}
      iconColor={w.over ? "#EF4444" : "#F59E0B"}
      primary={primary}
      secondary={
        <span>
          {fmtMoney(w.spent, w.currency || "USD")} of {fmtMoney(w.approved, w.currency || "USD")}
          {w.over && (
            <> · <span className="text-red-300">{fmtMoney(overAmt, w.currency || "USD", { maximumFractionDigits: 0 })} over</span></>
          )}
        </span>
      }
      to={to}
      testid={kind === "project" ? `attention-project-warning-${w.project_id}` : `attention-entity-warning-${w.entity_id}`}
    />
  );
}

function RightNowSettings({ hiddenSections, onToggleHidden }) {
  const hiddenSet = new Set(hiddenSections || []);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="attention-settings-btn"
          className="w-7 h-7 rounded-md bg-white/[0.04] flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors"
          title="Hide or show sections"
        >
          <Settings2 className="w-3.5 h-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#151A23] border-white/10 text-white w-56">
        <DropdownMenuLabel className="text-white/50 text-[11px] uppercase tracking-wider">
          Show on homepage
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/10" />
        {RIGHT_NOW_SECTIONS.map((s) => (
          <DropdownMenuCheckboxItem
            key={s.key}
            checked={!hiddenSet.has(s.key)}
            onCheckedChange={() => onToggleHidden(s.key)}
            onSelect={(e) => e.preventDefault()}
            data-testid={`attention-hide-toggle-${s.key}`}
            className="text-white hover:bg-white/10 focus:bg-white/10"
          >
            {s.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * AttentionPanel — Right Now executive digest column.
 *
 * Preferences (collapsed sections + hidden sections) are OWNED by the parent
 * page and passed in as props so they persist via /lib/hqPrefs across
 * sessions. Section prioritization order is fixed here:
 *   1. Overdue tasks       (urgent, action required now)
 *   2. Project Health      (structural risk indicator)
 *   3. Pending decisions   (executive-only action)
 *   4. Upcoming            (informational, forward)
 *   5. Budget warnings     (numeric, non-blocking)
 * Hidden sections are filtered out entirely so they don't consume vertical
 * space. Data is unchanged everywhere else in HQ.
 */
export default function AttentionPanel({
  data,
  onDataChange,
  hiddenSections = [],
  collapsedSections = [],
  onToggleHidden = () => {},
  onRestoreAll = null,
  onToggleCollapsed = () => {},
  presetSwitcher = null,
}) {
  const overdue = data?.overdue_tasks || [];
  const upcoming = data?.upcoming_events || [];
  const decisions = data?.pending_decisions || [];
  const entityWarnings = data?.budget_warnings || [];
  const projectWarnings = data?.project_budget_warnings || [];
  const healthWarnings = data?.project_health_warnings || [];
  // Dedupe project-budget-warnings that are already represented in health
  // warnings via a budget_* reason on the same project — avoids the same
  // "78% used" showing twice in the panel.
  const healthBudgetProjectIds = new Set(
    healthWarnings
      .filter(hw => (hw.reasons || []).some(r => (r.code || "").startsWith("budget_")))
      .map(hw => hw.project_id)
  );
  const filteredProjectWarnings = projectWarnings.filter(w => !healthBudgetProjectIds.has(w.project_id));
  const combinedWarnings = [
    ...entityWarnings.map((w) => ({ ...w, __kind: "entity", __key: `e-${w.entity_id}` })),
    ...filteredProjectWarnings.map((w) => ({ ...w, __kind: "project", __key: `p-${w.project_id}` })),
  ];
  const warningsCount = combinedWarnings.length;

  const [expanded, setExpanded] = useState({ overdue: false, upcoming: false, decisions: false, warnings: false, health: false });
  const toggle = (key) => setExpanded((s) => ({ ...s, [key]: !s[key] }));
  const hiddenSet = new Set(hiddenSections || []);
  const collapsedSet = new Set(collapsedSections || []);
  const isVisible = (key) => !hiddenSet.has(key);
  const isCollapsed = (key) => collapsedSet.has(key);

  const handleResolved = (noteId) => {
    if (onDataChange && data) {
      onDataChange({ ...data, pending_decisions: (data.pending_decisions || []).filter((n) => n.id !== noteId) });
    }
  };

  const overdueShown = expanded.overdue ? overdue : overdue.slice(0, PREVIEW.overdue);
  const upcomingShown = expanded.upcoming ? upcoming : upcoming.slice(0, PREVIEW.upcoming);
  const decisionsShown = expanded.decisions ? decisions : decisions.slice(0, PREVIEW.decisions);
  const warningsShown = expanded.warnings ? combinedWarnings : combinedWarnings.slice(0, PREVIEW.warnings);
  const healthShown = expanded.health ? healthWarnings : healthWarnings.slice(0, PREVIEW.health);

  // If EVERY section is hidden, show a re-enable prompt so the panel is never
  // an unresponsive empty rail.
  const allHidden = RIGHT_NOW_SECTIONS.every((s) => hiddenSet.has(s.key));

  return (
    <div className="flex flex-col w-full h-full min-h-0" data-testid="attention-panel">
      <div className="p-6 pt-4 md:pt-6 flex-1 overflow-y-auto">
        <div className="mb-4 flex items-start justify-between gap-2">
          <div>
            <div className="text-eyebrow mb-1">Requires Attention</div>
            <h2 className="font-display text-lg font-semibold">Right now</h2>
          </div>
          <RightNowSettings hiddenSections={hiddenSections} onToggleHidden={onToggleHidden} />
        </div>

        {presetSwitcher}

        {allHidden && (
          <div className="mb-6 rounded-lg border border-dashed border-white/10 p-4 text-center text-xs text-white/50" data-testid="attention-all-hidden">
            All sections are hidden.
            <button
              type="button"
              onClick={() => (onRestoreAll ? onRestoreAll() : (hiddenSections || []).forEach((k) => onToggleHidden(k)))}
              className="mt-2 mx-auto flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-white/70 hover:text-white transition-colors"
              data-testid="attention-restore-all"
            >
              <Eye className="w-3 h-3" /> Restore all
            </button>
          </div>
        )}

        {isVisible("overdue") && (
          <Section
            sectionKey="overdue"
            title="Overdue Tasks"
            count={overdue.length}
            collapsed={isCollapsed("overdue")}
            onToggleCollapsed={() => onToggleCollapsed("overdue")}
          >
            {overdue.length === 0 && <div className="text-xs text-white/40">Nothing overdue.</div>}
            {overdueShown.map((t) => (
              <Item
                key={t.id}
                icon={AlertCircle}
                iconColor="#EC4899"
                primary={t.name}
                secondary={`Due ${t.due_date} · ${t.priority || "Normal"}`}
                to="/tasks"
                testid={`attention-overdue-${t.id}`}
              />
            ))}
            <ViewAllToggle
              expanded={expanded.overdue}
              total={overdue.length}
              previewCount={PREVIEW.overdue}
              onToggle={() => toggle("overdue")}
              testid="attention-view-all-overdue"
            />
          </Section>
        )}

        {isVisible("health") && (
          <Section
            sectionKey="health"
            title="Project Health"
            count={healthWarnings.length}
            collapsed={isCollapsed("health")}
            onToggleCollapsed={() => onToggleCollapsed("health")}
          >
            {healthWarnings.length === 0 && <div className="text-xs text-white/40">All projects on track.</div>}
            {healthShown.map((h) => {
              const color = "#EF4444";
              return (
                <Item
                  key={h.project_id}
                  icon={Activity}
                  iconColor={color}
                  primary={<><span className="text-white/60">{h.entity_name}</span> · {h.project_name}</>}
                  right={<span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border" style={{ color, borderColor: `${color}55`, backgroundColor: `${color}12` }}>{h.status}</span>}
                  secondary={(h.reason_text || []).slice(0,2).join(" · ")}
                  to={`/entity/${h.entity_id}?tab=projects`}
                  testid={`attention-health-${h.project_id}`}
                />
              );
            })}
            <ViewAllToggle
              expanded={expanded.health}
              total={healthWarnings.length}
              previewCount={PREVIEW.health}
              onToggle={() => toggle("health")}
              testid="attention-view-all-health"
            />
          </Section>
        )}

        {isVisible("decisions") && (
          <Section
            sectionKey="decisions"
            title="Pending Decisions"
            count={decisions.length}
            collapsed={isCollapsed("decisions")}
            onToggleCollapsed={() => onToggleCollapsed("decisions")}
          >
            {decisions.length === 0 && <div className="text-xs text-white/40">No open decisions.</div>}
            {decisionsShown.map((n) => (
              <DecisionItem key={n.id} note={n} onResolved={handleResolved} />
            ))}
            <ViewAllToggle
              expanded={expanded.decisions}
              total={decisions.length}
              previewCount={PREVIEW.decisions}
              onToggle={() => toggle("decisions")}
              testid="attention-view-all-decisions"
            />
          </Section>
        )}

        {isVisible("upcoming") && (
          <Section
            sectionKey="upcoming"
            title="Upcoming (7 days)"
            count={upcoming.length}
            collapsed={isCollapsed("upcoming")}
            onToggleCollapsed={() => onToggleCollapsed("upcoming")}
          >
            {upcoming.length === 0 && <div className="text-xs text-white/40">Nothing scheduled.</div>}
            {upcomingShown.map((e) => {
              const meta = SOURCE_META[e.source_type] || SOURCE_META.event;
              return (
                <Item
                  key={e.id}
                  icon={CalendarClock}
                  iconColor={meta.color}
                  primary={e.title}
                  right={<TypeBadge type={e.source_type} />}
                  secondary={
                    e.all_day
                      ? new Date(e.starts_at).toLocaleDateString([], { month: "short", day: "numeric" })
                      : new Date(e.starts_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
                  }
                  to={meta.route(e)}
                  testid={`attention-upcoming-${e.source_type}-${e.source_id}`}
                />
              );
            })}
            <ViewAllToggle
              expanded={expanded.upcoming}
              total={upcoming.length}
              previewCount={PREVIEW.upcoming}
              onToggle={() => toggle("upcoming")}
              testid="attention-view-all-upcoming"
            />
          </Section>
        )}

        {isVisible("warnings") && (
          <Section
            sectionKey="warnings"
            title="Budget Warnings"
            count={warningsCount}
            collapsed={isCollapsed("warnings")}
            onToggleCollapsed={() => onToggleCollapsed("warnings")}
          >
            {warningsCount === 0 && <div className="text-xs text-white/40">All budgets healthy.</div>}
            {warningsShown.map((w) => (
              <WarningItem key={w.__key} w={w} kind={w.__kind} />
            ))}
            <ViewAllToggle
              expanded={expanded.warnings}
              total={warningsCount}
              previewCount={PREVIEW.warnings}
              onToggle={() => toggle("warnings")}
              testid="attention-view-all-warnings"
            />
          </Section>
        )}
      </div>
    </div>
  );
}
